<template>
  <div class="custom-html-editor d-flex flex-row">

    <!-- Left: Code Editor -->
    <div class="editor-main d-flex flex-column flex-grow-1">

      <!-- HTML Code Editor -->
      <div class="html-editor-section flex-grow-1 d-flex flex-column">
        <div class="section-header d-flex align-center justify-space-between px-3 py-1">
          <v-btn-toggle v-model="viewMode" mandatory dense class="view-toggle">
            <v-btn value="code" text x-small>
              <v-icon left x-small>mdi-code-tags</v-icon>
              {{$t('custom_html_view_code')}}
            </v-btn>
            <v-btn value="visual" text x-small>
              <v-icon left x-small>mdi-eye</v-icon>
              {{$t('custom_html_view_visual')}}
            </v-btn>
          </v-btn-toggle>
          <div>
            <v-btn v-if="localHtml && localHtml.trim()" text x-small @click="changeTemplateModal = true">
              <v-icon left x-small>mdi-file-swap-outline</v-icon>
              {{$t('custom_html_change_template')}}
            </v-btn>
            <v-btn v-show="viewMode === 'code'" text x-small @click="formatCode">
              <v-icon left x-small>mdi-format-indent-increase</v-icon>
              {{$t('custom_html_format')}}
            </v-btn>
            <v-btn text x-small @click="variablesPanel = !variablesPanel">
              <v-icon left x-small>mdi-code-braces</v-icon>
              {{$t('custom_html_variables_title')}}
            </v-btn>
            <v-btn v-if="isAllowed('is_ste')" text x-small @click="chatOpen = !chatOpen" :color="chatOpen ? 'primary' : ''">
              <v-icon left x-small>mdi-robot</v-icon>
              {{$t('custom_html_ai_chat')}}
            </v-btn>
          </div>
        </div>

        <!-- Variables Reference Panel -->
        <v-expand-transition>
          <div v-if="variablesPanel" class="variables-panel px-3 py-2">
            <div class="variables-hint mb-1">{{$t('custom_html_variables_hint')}}</div>
            <div class="variables-grid">
              <div
                v-for="variable in availableVariables"
                :key="variable.path"
                class="variable-item"
                @mousedown.prevent
                @click="insertVariable(variable.path)"
              >
                <code>{{ '{' + variable.path + '}' }}</code>
                <span class="variable-desc ml-1">{{ variable.name }}</span>
              </div>
            </div>
          </div>
        </v-expand-transition>

        <div v-if="isLargeDoc && viewMode === 'code'" class="large-file-notice px-3 py-1 d-flex align-center">
          <v-icon x-small left color="amber darken-3">mdi-information-outline</v-icon>
          <span>{{ $t('custom_html_large_file_notice', { size: largeFileSizeLabel }) }}</span>
        </div>

        <div v-show="viewMode === 'code'" class="codemirror-wrapper flex-grow-1">
          <codemirror
            ref="editor"
            v-model="localHtml"
            :options="editorOptions"
            @input="onHtmlInput"
          ></codemirror>
        </div>

        <div v-show="viewMode === 'visual'" class="simplified-view-wrapper flex-grow-1">
          <simplified-html-view
            ref="simplifiedView"
            :tab-id="tabId"
            :selected-block-id.sync="selectedBlockForAI"
            @block-edited="onSimplifiedBlockEdit"
            @block-deleted="onBlockDeleted"
            @block-hover="onBlockHover"
            @template-selected="onTemplateSelected"
            @start-scratch="viewMode = 'code'"
          ></simplified-html-view>
        </div>
      </div>
    </div>

    <!-- Right: AI Chat Panel -->
    <transition name="slide-chat">
      <div v-if="chatOpen" class="chat-panel d-flex flex-column">
        <div class="chat-header d-flex align-center justify-space-between px-3 py-2">
          <span class="font-weight-bold">{{$t('custom_html_ai_chat_title')}}</span>
          <v-btn icon x-small @click="chatOpen = false">
            <v-icon small>mdi-close</v-icon>
          </v-btn>
        </div>

        <!-- Messages -->
        <div class="chat-messages flex-grow-1" ref="chatMessages">
          <div v-if="!chatHistory.length" class="chat-empty pa-4">
            <div class="chat-empty-icon mb-2">
              <v-icon large color="grey lighten-1">mdi-robot-outline</v-icon>
            </div>
            <div class="font-weight-light text-center grey--text">
              {{$t('custom_html_ai_chat_empty')}}
            </div>
          </div>
          <div v-for="(msg, index) in chatHistory" :key="index" class="chat-message pa-3" :class="'chat-' + msg.role">
            <div class="chat-role mb-1">
              <v-icon x-small class="mr-1">{{ msg.role === 'user' ? 'mdi-account' : 'mdi-robot' }}</v-icon>
              <span class="font-weight-bold">{{ msg.role === 'user' ? $t('custom_html_ai_chat_you') : $t('custom_html_ai_chat_ai') }}</span>
            </div>
            <div class="chat-content">{{ msg.content }}</div>
          </div>
          <div v-if="aiGenerating" class="chat-message chat-assistant pa-3">
            <div class="chat-role mb-1">
              <v-icon x-small class="mr-1">mdi-robot</v-icon>
              <span class="font-weight-bold">{{$t('custom_html_ai_chat_ai')}}</span>
            </div>
            <div class="chat-content typing">
              <span class="dot"></span><span class="dot"></span><span class="dot"></span>
            </div>
          </div>
        </div>

        <!-- Undo/Redo toolbar -->
        <div v-if="canUndo || canRedo" class="chat-undo-bar d-flex align-center justify-center pa-1">
          <v-btn text x-small :disabled="!canUndo" @click="undoAiChange">
            <v-icon left x-small>mdi-undo</v-icon>
            {{$t('custom_html_undo')}}
          </v-btn>
          <v-btn text x-small :disabled="!canRedo" @click="redoAiChange">
            <v-icon left x-small>mdi-redo</v-icon>
            {{$t('custom_html_redo')}}
          </v-btn>
        </div>

        <!-- Input -->
        <div class="chat-input-area pa-2">
          <div class="d-flex align-center">
            <v-text-field
              v-model="chatInput"
              :placeholder="chatHistory.length ? $t('custom_html_ai_chat_followup') : $t('custom_html_ai_chat_placeholder')"
              dense
              outlined
              hide-details
              class="chat-input"
              @keydown.enter.exact="onChatEnter"
            ></v-text-field>
            <v-btn
              icon
              color="primary"
              class="ml-1"
              :disabled="!chatInput.trim() || aiGenerating"
              :loading="aiGenerating"
              @click="sendMessage"
            >
              <v-icon>mdi-send</v-icon>
            </v-btn>
          </div>
          <div v-if="aiError" class="chat-error mt-1 px-1">
            <span class="red--text text-caption">{{ aiError }}</span>
          </div>
        </div>
      </div>
    </transition>

    <!-- Change Template confirmation -->
    <v-dialog v-model="changeTemplateModal" width="500">
      <v-card>
        <v-card-title class="headline">{{$t('custom_html_change_template_title')}}</v-card-title>
        <v-card-text class="py-4">
          {{$t('custom_html_change_template_warning')}}
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="changeTemplateModal = false">{{$t('cancel')}}</v-btn>
          <v-btn text color="red" @click="confirmChangeTemplate">{{$t('confirm')}}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { codemirror } from 'vue-codemirror'
import SimplifiedHtmlView from './SimplifiedHtmlView.vue'
import formatHtml from '@/utils/formatHtml'
import 'codemirror/lib/codemirror.css'
import 'codemirror/theme/material-darker.css'
import 'codemirror/mode/htmlmixed/htmlmixed'
import 'codemirror/addon/edit/closetag'
import 'codemirror/addon/edit/closebrackets'
import 'codemirror/addon/fold/xml-fold'

// Above this size (in characters ~ bytes for typical HTML), CodeMirror's line
// wrapping + htmlmixed tokenizing + fold scanning block the main thread long
// enough to freeze the page on paste/load. We degrade to plain text in that
// case. SMALL threshold gives hysteresis so editing near the boundary doesn't
// flap the editor mode back and forth.
const LARGE_DOC_THRESHOLD = 120 * 1024
const SMALL_DOC_THRESHOLD = 60 * 1024
// Hard ceiling: content above this is refused outright rather than degraded,
// since even plain-text editing of multi-MB documents is sluggish.
const HARD_DOC_LIMIT = 5 * 1024 * 1024

export default {
  name: 'CustomHtml',
  props: ['tabId'],
  components: {
    codemirror,
    SimplifiedHtmlView
  },
  data () {
    return {
      localHtml: '',
      viewMode: 'visual',
      selectedBlockForAI: null,
      chatOpen: false,
      changeTemplateModal: false,
      chatInput: '',
      chatHistory: [],
      aiError: null,
      variablesPanel: false,
      lastAiAction: null,
      debounceTimer: null,
      isLargeDoc: false,
      editorOptions: {
        mode: 'htmlmixed',
        theme: 'material-darker',
        lineNumbers: true,
        lineWrapping: true,
        autoCloseTags: true,
        autoCloseBrackets: true,
        tabSize: 2,
        indentWithTabs: false,
        foldGutter: true
      }
    }
  },
  computed: {
    ...mapGetters(['defaultLanguage', 'aiGenerating', 'getBlocksMeta', 'availableVariables', 'previewClickEvent', 'isAllowed']),

    canUndo () {
      const has = this.$store.getters.hasHtmlSnapshot(this.tabId)
      return has && (this.lastAiAction === 'generate' || this.lastAiAction === 'redo' || this.lastAiAction === null)
    },
    canRedo () {
      const has = this.$store.getters.hasHtmlSnapshot(this.tabId)
      return has && this.lastAiAction === 'undo'
    },
    largeFileSizeLabel () {
      return this.formatBytes(this.localHtml ? this.localHtml.length : 0)
    }
  },
  watch: {
    defaultLanguage () {
      this.syncFromStore()
    },
    localHtml () {
      // Catches large content arriving programmatically (template, AI, store
      // sync) as well as pastes where the clipboard couldn't be pre-inspected.
      this.evaluatePerfMode()
    },
    viewMode (mode) {
      if (mode === 'visual') {
        this.$store.dispatch('extractHtmlBlocks', { tabId: this.tabId })
        this.selectedBlockForAI = null
      } else {
        this.$store.commit('SET_HOVERED_BLOCK_ID', null)
        this.$nextTick(() => {
          const cm = this.$refs.editor && this.$refs.editor.codemirror
          if (cm) cm.refresh()
        })
      }
    },
    'previewClickEvent.seq' () {
      const blockId = this.previewClickEvent.blockId
      if (!blockId) return
      if (this.viewMode !== 'visual') this.viewMode = 'visual'
      this.selectedBlockForAI = blockId
      this.$nextTick(() => {
        if (this.$refs.simplifiedView) {
          this.$refs.simplifiedView.scrollBlockIntoView(blockId)
          if (!this.chatOpen) {
            this.$refs.simplifiedView.focusBlock(blockId)
          }
        }
      })
    }
  },
  created () {
    this.syncFromStore()
    // If the tab already holds a large document, initialise the editor in
    // degraded mode so the very first mount doesn't freeze.
    this.evaluatePerfMode()
  },
  mounted () {
    const cm = this.getCm()
    if (cm) {
      // Pre-empt the freeze: degrade *before* CodeMirror lays out the paste.
      cm.on('paste', this.onCmPaste)
    }
    if (this.viewMode === 'code') {
      this.$nextTick(() => {
        if (cm) cm.refresh()
      })
    }
  },
  methods: {
    syncFromStore () {
      const tabData = this.$store.getters.getTabData(this.tabId)
      if (tabData && tabData.html) {
        this.localHtml = tabData.html || ''
      }
    },

    getCm () {
      return this.$refs.editor && this.$refs.editor.codemirror
    },

    formatBytes (bytes) {
      if (bytes >= 1024 * 1024) return (bytes / 1024 / 1024).toFixed(1) + ' MB'
      return Math.round(bytes / 1024) + ' KB'
    },

    onCmPaste (cm, e) {
      try {
        const data = e.clipboardData || window.clipboardData
        const pasted = data ? data.getData('text') : ''
        const projected = this.localHtml.length + pasted.length

        // Refuse outright above the hard limit — cancel the paste so nothing
        // is inserted and the editor never has to render it.
        if (projected > HARD_DOC_LIMIT) {
          e.preventDefault()
          this.$store.dispatch('showSnackBar', {
            message: this.$t('custom_html_file_too_large', {
              size: this.formatBytes(projected),
              limit: this.formatBytes(HARD_DOC_LIMIT)
            })
          })
          return
        }

        if (!this.isLargeDoc && projected >= LARGE_DOC_THRESHOLD) {
          this.setPerfMode(true)
        }
      } catch (err) {
        // Clipboard not readable here; the localHtml watcher will degrade once
        // the pasted content lands (a brief freeze, but no broken state).
      }
    },

    evaluatePerfMode () {
      const len = this.localHtml ? this.localHtml.length : 0
      if (!this.isLargeDoc && len >= LARGE_DOC_THRESHOLD) {
        this.setPerfMode(true)
      } else if (this.isLargeDoc && len < SMALL_DOC_THRESHOLD) {
        this.setPerfMode(false)
      }
    },

    // Toggle the expensive editor features. Applied synchronously to the live
    // instance (so it takes effect before the pending render) and mirrored onto
    // editorOptions so a fresh mount uses the right options.
    setPerfMode (isLarge) {
      this.isLargeDoc = isLarge
      const opts = isLarge
        ? { lineWrapping: false, foldGutter: false, mode: null }
        : { lineWrapping: true, foldGutter: true, mode: 'htmlmixed' }
      const cm = this.getCm()
      if (cm) {
        cm.setOption('lineWrapping', opts.lineWrapping)
        cm.setOption('foldGutter', opts.foldGutter)
        cm.setOption('mode', opts.mode)
      }
      this.editorOptions.lineWrapping = opts.lineWrapping
      this.editorOptions.foldGutter = opts.foldGutter
      this.editorOptions.mode = opts.mode
    },

    onHtmlInput () {
      clearTimeout(this.debounceTimer)
      this.debounceTimer = setTimeout(() => {
        this.$store.dispatch('updateCustomHtmlContent', {
          tabId: this.tabId,
          html: this.localHtml
        })
      }, 500)
    },

    onChatEnter (e) {
      e.preventDefault()
      this.sendMessage()
    },

    onTemplateSelected (html) {
      this.localHtml = html
      this.$store.dispatch('updateCustomHtmlContent', {
        tabId: this.tabId,
        html
      })
    },

    confirmChangeTemplate () {
      this.localHtml = ''
      this.$store.dispatch('updateCustomHtmlContent', {
        tabId: this.tabId,
        html: ''
      })
      this.viewMode = 'visual'
      this.changeTemplateModal = false
    },

    onBlockHover (blockId) {
      this.$store.commit('SET_HOVERED_BLOCK_ID', blockId)
    },

    async onBlockDeleted ({ blockId }) {
      await this.$store.dispatch('deleteHtmlBlock', {
        tabId: this.tabId,
        blockId
      })
      const tabData = this.$store.getters.getTabData(this.tabId)
      if (tabData) {
        this.localHtml = tabData.html || ''
      }
      if (this.selectedBlockForAI === blockId) {
        this.selectedBlockForAI = null
      }
    },

    async onSimplifiedBlockEdit ({ blockId, value }) {
      await this.$store.dispatch('updateHtmlBlock', {
        tabId: this.tabId,
        blockId,
        value
      })
      // Sync local HTML from store
      const tabData = this.$store.getters.getTabData(this.tabId)
      if (tabData) {
        this.localHtml = tabData.html || ''
      }
    },

    async sendMessage () {
      const text = this.chatInput.trim()
      if (!text || this.aiGenerating) return

      this.aiError = null
      this.chatInput = ''

      // Build prompt with optional section targeting
      let prompt = text
      if (this.selectedBlockForAI && this.viewMode === 'visual') {
        const meta = this.getBlocksMeta(this.tabId)
        const blockMeta = meta[this.selectedBlockForAI]
        const blocks = this.$store.getters.getHtmlBlocks(this.tabId)
        const blockData = blocks[this.selectedBlockForAI]
        if (blockMeta && blockData) {
          const content = blockData[this.defaultLanguage] || ''
          const excerpt = content.length > 100 ? content.substring(0, 100) + '...' : content
          prompt = `[Modify the <${blockMeta.tag}> element containing "${excerpt}"]\n${text}`
        }
      }

      // Build history for the AI from prior turns (excluding system notices
      // like undo/redo). Use promptForAI when set so block-targeting prefixes
      // are preserved for the model without cluttering the visible chat.
      const history = this.chatHistory
        .filter(m => !m.noContext)
        .slice(-20)
        .map(m => ({ role: m.role, content: m.promptForAI || m.content }))

      // Add user message (show original text to user, not the augmented prompt)
      const userEntry = { role: 'user', content: text }
      if (prompt !== text) userEntry.promptForAI = prompt
      this.chatHistory.push(userEntry)

      this.scrollChat()

      try {
        const { html, summary, message } = await this.$store.dispatch('generateAIHtml', {
          tabId: this.tabId,
          lang: this.defaultLanguage,
          prompt,
          history
        })

        if (html) {
          this.chatHistory.push({ role: 'assistant', content: summary || this.$t('custom_html_ai_done') })
          this.localHtml = html
          this.lastAiAction = 'generate'
        } else if (message) {
          this.chatHistory.push({ role: 'assistant', content: message })
        }
        this.scrollChat()
      } catch (err) {
        this.aiError = err.message || this.$t('custom_html_ai_error')
        // Remove the failed user message from history
        this.chatHistory.pop()
      }
    },

    undoAiChange () {
      this.$store.commit('SWAP_AI_HTML', { tabId: this.tabId })
      this.lastAiAction = 'undo'
      this.localHtml = this.$store.getters.getTabData(this.tabId).html || ''
      this.chatHistory.push({ role: 'assistant', content: this.$t('custom_html_ai_reverted'), noContext: true })
      this.scrollChat()
    },

    redoAiChange () {
      this.$store.commit('SWAP_AI_HTML', { tabId: this.tabId })
      this.lastAiAction = 'redo'
      this.localHtml = this.$store.getters.getTabData(this.tabId).html || ''
      this.chatHistory.push({ role: 'assistant', content: this.$t('custom_html_ai_reapplied'), noContext: true })
      this.scrollChat()
    },

    scrollChat () {
      this.$nextTick(() => {
        const el = this.$refs.chatMessages
        if (el) {
          el.scrollTop = el.scrollHeight
        }
      })
    },

    formatCode () {
      const formatted = formatHtml(this.localHtml)
      if (formatted === this.localHtml) return
      this.localHtml = formatted
      this.$store.dispatch('updateCustomHtmlContent', { tabId: this.tabId, html: formatted })
    },

    insertVariable (path) {
      const token = `{${path}}`

      if (this.viewMode === 'code') {
        const cm = this.$refs.editor && this.$refs.editor.codemirror
        if (cm) {
          cm.getDoc().replaceRange(token, cm.getDoc().getCursor())
          cm.focus()
        } else {
          navigator.clipboard.writeText(token)
        }
        return
      }

      const active = document.activeElement
      if (!active || !active.closest || !active.closest('.simplified-view-wrapper')) return

      if (active.isContentEditable) {
        document.execCommand('insertText', false, token)
        return
      }

      if (active.tagName === 'INPUT' || active.tagName === 'TEXTAREA') {
        const start = active.selectionStart != null ? active.selectionStart : active.value.length
        const end = active.selectionEnd != null ? active.selectionEnd : start
        active.value = active.value.slice(0, start) + token + active.value.slice(end)
        const caret = start + token.length
        active.setSelectionRange(caret, caret)
        active.dispatchEvent(new Event('input', { bubbles: true }))
      }
    }
  },
  beforeDestroy () {
    clearTimeout(this.debounceTimer)
    const cm = this.getCm()
    if (cm) cm.off('paste', this.onCmPaste)
    this.$store.commit('SET_HOVERED_BLOCK_ID', null)
  }
}
</script>

<style lang="sass">
// Unscoped so CodeMirror and Vuetify tab layout integrate properly
.custom-html-editor
  height: calc(100vh - 160px)
  overflow: hidden
  background: white
  display: flex
  flex-direction: row

.custom-html-editor .editor-main
  flex: 1
  min-width: 0
  overflow: hidden
  display: flex
  flex-direction: column

.custom-html-editor .html-editor-section
  flex: 1
  display: flex
  flex-direction: column
  overflow: hidden

.custom-html-editor .section-header
  background: #f5f5f5
  border-bottom: 1px solid #e0e0e0
  font-size: 12px
  flex-shrink: 0

// Variables panel
.custom-html-editor .variables-panel
  background: #fafafa
  border-bottom: 1px solid #e0e0e0
  flex-shrink: 0

.custom-html-editor .variables-hint
  font-size: 11px
  color: #666

.custom-html-editor .variables-grid
  display: flex
  flex-wrap: wrap
  gap: 4px

.custom-html-editor .variable-item
  font-size: 11px
  cursor: pointer
  padding: 2px 6px
  border-radius: 3px
  white-space: nowrap
  &:hover
    background: #e8e8e8
  code
    background: #fff
    padding: 0 3px
    border-radius: 2px
    border: 1px solid #ddd
    font-size: 10px
  .variable-desc
    color: #888

// Large-file performance notice
.custom-html-editor .large-file-notice
  background: #fff8e1
  border-bottom: 1px solid #ffe082
  color: #8a6d00
  font-size: 11px
  flex-shrink: 0

// View toggle
.custom-html-editor .view-toggle
  .v-btn
    text-transform: none !important

// Simplified view
.custom-html-editor .simplified-view-wrapper
  flex: 1
  overflow: hidden

// CodeMirror
.custom-html-editor .codemirror-wrapper
  flex: 1
  overflow: hidden
  .vue-codemirror
    height: 100%
    .CodeMirror
      height: 100%
      font-size: 12px
      line-height: 1.5

// Chat panel
.custom-html-editor .chat-panel
  width: 340px
  min-width: 340px
  border-left: 2px solid #e0e0e0
  background: #fafafa
  overflow: hidden
  display: flex
  flex-direction: column

.custom-html-editor .slide-chat-enter-active,
.custom-html-editor .slide-chat-leave-active
  transition: all 0.2s ease

.custom-html-editor .slide-chat-enter,
.custom-html-editor .slide-chat-leave-to
  width: 0
  min-width: 0
  opacity: 0

.custom-html-editor .chat-header
  background: #fff
  border-bottom: 1px solid #e0e0e0
  font-size: 13px
  flex-shrink: 0

.custom-html-editor .chat-messages
  overflow-y: auto
  flex: 1

.custom-html-editor .chat-empty
  text-align: center
  padding-top: 60px !important

.custom-html-editor .chat-message
  border-bottom: 1px solid #eee

.custom-html-editor .chat-user
  background: #fff

.custom-html-editor .chat-assistant
  background: #f0f7ff

.custom-html-editor .chat-role
  font-size: 11px
  color: #666

.custom-html-editor .chat-content
  font-size: 13px
  line-height: 1.5
  white-space: pre-wrap
  word-break: break-word

.custom-html-editor .chat-undo-bar
  border-top: 1px solid #e0e0e0
  border-bottom: 1px solid #e0e0e0
  background: #f5f5f5
  flex-shrink: 0

.custom-html-editor .chat-input-area
  border-top: 1px solid #e0e0e0
  background: #fff
  flex-shrink: 0

.custom-html-editor .chat-input
  .v-input__slot
    min-height: 36px !important

.custom-html-editor .chat-error
  font-size: 11px

// Typing animation
.custom-html-editor .typing
  display: flex
  gap: 4px
  align-items: center
  .dot
    width: 6px
    height: 6px
    background: #999
    border-radius: 50%
    animation: typing-bounce 1.4s infinite ease-in-out both
    &:nth-child(1)
      animation-delay: 0s
    &:nth-child(2)
      animation-delay: 0.2s
    &:nth-child(3)
      animation-delay: 0.4s

@keyframes typing-bounce
  0%, 80%, 100%
    transform: scale(0.6)
    opacity: 0.4
  40%
    transform: scale(1)
    opacity: 1
</style>
